import { HelpSection } from './help-center.types';

/**
 * Contenido del Centro de ayuda. Describe la aplicación tal como funciona: al cambiar una pantalla,
 * actualizar aquí su sección (las etiquetas entre comillas son las que ve el usuario).
 */
export const HELP_SECTIONS: HelpSection[] = [
    // ------------------------------------------------------------------ PRIMEROS PASOS
    {
        id: 'primeros-pasos',
        title: 'Primeros pasos',
        icon: 'rocket_launch',
        summary: 'Qué es HoneyTokens, cómo moverte por la aplicación y por dónde empezar.',
        articles: [
            {
                id: 'que-es',
                title: '¿Qué es HoneyTokens?',
                keywords: ['introducción', 'señuelo', 'deception', 'engaño'],
                blocks: [
                    { type: 'p', text: 'Un **honeytoken** es un señuelo digital: un documento, un enlace, una imagen, un ejecutable, un servidor SSH o un buzón de correo que parece un recurso real de tu organización, pero que nadie debería usar. Cuando alguien lo abre, lo descarga o lo utiliza, HoneyTokens registra un **evento de seguridad** con la información del acceso (IP, ubicación, navegador, red…).' },
                    { type: 'p', text: 'Como un usuario legítimo no tiene motivos para tocar un señuelo, cada evento es una señal muy fiable de que algo va mal: un intruso, una fuga de información o un uso indebido de los datos.' },
                    { type: 'p', text: 'Con la aplicación puedes:' },
                    { type: 'list', items: [
                        'Organizar tus señuelos en **proyectos** y compartirlos con tu equipo.',
                        'Crear **honeytokens** de 12 tipos y descargarlos o incrustarlos donde quieras vigilar.',
                        'Revisar los **eventos de seguridad** que generan, con análisis por IA y de la IP de origen.',
                        'Recibir y gestionar **alertas** cuando un evento es grave.',
                        'Investigar IPs y eventos con **inteligencia de amenazas**.',
                        'Publicar artículos con honeytokens integrados (**Detección de Contenido**) para saber quién consulta y reutiliza ese contenido.',
                    ] },
                ],
            },
            {
                id: 'como-empezar',
                title: 'Cómo empezar: el flujo básico',
                keywords: ['empezar', 'primer', 'tutorial', 'guía rápida'],
                blocks: [
                    { type: 'steps', items: [
                        '**Crea un proyecto** para agrupar los señuelos de un mismo cliente, sistema o campaña (lo hacen el administrador y los gestores de proyectos).',
                        '**Comparte el proyecto** con las personas que lo van a usar, con permiso de lectura o de escritura.',
                        '**Crea uno o varios honeytokens** dentro del proyecto, eligiendo el tipo que encaje con lo que quieres vigilar.',
                        '**Despliégalos**: deja el documento en una carpeta compartida, pega el enlace o el código en una web, instala el servidor SSH señuelo…',
                        '**Vigila los eventos**: cada vez que alguien toque un señuelo aparecerá en «Eventos de seguridad» y en la página del proyecto.',
                        '**Gestiona las alertas**: los eventos graves generan alertas que puedes reconocer, investigar y resolver.',
                    ] },
                    { type: 'tip', text: 'Si tu rol es Editor o Lector y no ves ningún proyecto, pide a tu gestor de proyectos que te asigne a uno.' },
                    { type: 'links', links: [
                        { label: 'Ir a Proyectos', route: '/projects' },
                        { label: 'Ver tipos de honeytoken', route: '/ayuda/honeytokens' },
                    ] },
                ],
            },
            {
                id: 'navegacion',
                title: 'Moverte por la aplicación',
                keywords: ['menú', 'barra lateral', 'migas', 'navegación', 'pestañas', 'crear nuevo'],
                blocks: [
                    { type: 'p', text: 'El **menú lateral** agrupa las pantallas:' },
                    { type: 'table', headers: ['Grupo', 'Pantallas'], rows: [
                        ['Gestión', '«Proyectos», «Honeytokens», «Eventos de seguridad», «Alertas»'],
                        ['Inteligencia de Amenazas', '«Búsqueda de IPs», «Análisis de eventos»'],
                        ['Detección de Contenido', '«Solicitudes»'],
                        ['Centro de Conocimiento', '«Centro de ayuda», «Glosario de Seguridad»'],
                        ['Catálogos y Administración', 'Solo para administradores: tipos de honeytokens y de ataques, dominios, datos maestros y usuarios'],
                    ] },
                    { type: 'list', items: [
                        'El botón **«Crear Nuevo»** de la barra lateral es un atajo para crear un proyecto, un honeytoken o un usuario (según tu rol).',
                        'Los **nombres** de proyectos, honeytokens y eventos son enlaces: llevan a su página. Desde ahí, las **migas** de la parte superior («Proyectos › Proyecto 12 › …») te devuelven al nivel anterior.',
                        'Las páginas con **pestañas** guardan la pestaña en la dirección (por ejemplo `?tab=estadisticas`): puedes recargar, volver atrás o compartir el enlace y se abrirá en la misma pestaña.',
                        'En la barra superior tienes **pantalla completa**, tus **accesos directos** personalizados, la **campana de alertas** y tu **menú de usuario**.',
                    ] },
                ],
            },
            {
                id: 'inicio',
                title: 'La página de inicio',
                keywords: ['home', 'panel', 'dashboard', 'resumen', 'pdf'],
                blocks: [
                    { type: 'p', text: 'La página **Inicio** es un resumen global de los proyectos a los que tienes acceso:' },
                    { type: 'list', items: [
                        'Tarjeta **«Eventos de seguridad»**: total de eventos, IPs únicas y honeytokens, con accesos a proyectos, honeytokens y eventos.',
                        'Tarjeta **«Sistema de Alertas»**: total, críticas y sin resolver.',
                        'Tres informes en pestañas: **«Análisis de alertas»** (tendencia, tipos, países y alertas críticas recientes), **«Análisis IA de eventos»** (severidad, IPs y proyectos con más eventos, objetivos más atacados) y **«Análisis Geo de IPs»** (riesgo, VPN, Tor, países y mapa).',
                    ] },
                    { type: 'p', text: 'Cada informe tiene el botón **«Descargar PDF»**.' },
                    { type: 'tip', text: 'Si todavía no hay datos verás «¡Bienvenido a Honey Tokens!» con los primeros pasos que corresponden a tu rol.' },
                ],
            },
            {
                id: 'perfil',
                title: 'Tu perfil y tu sesión',
                keywords: ['contraseña', 'perfil', 'cerrar sesión', 'email', 'avatar'],
                blocks: [
                    { type: 'p', text: 'Pulsa tu **avatar** (la inicial de tu nombre, arriba a la derecha) para ver tu nombre, email y rol.' },
                    { type: 'list', items: [
                        '**«Actualizar perfil»**: cambia tu nombre, tu email o tu contraseña. La contraseña nueva debe incluir mayúscula, minúscula, número y un carácter especial.',
                        '**«Cerrar sesión»**: sale de la aplicación en este navegador.',
                    ] },
                ],
            },
        ],
    },

    // ------------------------------------------------------------------ ROLES
    {
        id: 'roles-permisos',
        title: 'Roles y permisos',
        icon: 'admin_panel_settings',
        summary: 'Qué puede hacer cada rol y cómo funcionan los permisos de lectura y escritura en cada proyecto.',
        articles: [
            {
                id: 'roles',
                title: 'Los cuatro roles',
                keywords: ['administrador', 'gestor', 'editor', 'lector', 'admin', 'project manager', 'writer', 'reader'],
                blocks: [
                    { type: 'p', text: 'Cada usuario tiene un **rol** para toda la aplicación y, además, un **permiso en cada proyecto** al que está asignado (lectura o escritura).' },
                    { type: 'table', headers: ['Rol', 'Qué puede hacer'], rows: [
                        ['**Administrador**', 'Todo: ve y gestiona todos los proyectos, usuarios, dominios y catálogos.'],
                        ['**Gestor de Proyectos**', 'Crea proyectos y, en los suyos (de los que es propietario), los edita, los elimina y asigna usuarios. En los proyectos donde tiene escritura gestiona honeytokens y demás recursos.'],
                        ['**Editor**', 'Trabaja en los proyectos que le asignan: con permiso de escritura crea, edita y elimina honeytokens, registra acciones, publica contenido…'],
                        ['**Lector**', 'Solo consulta: ve proyectos, honeytokens, eventos y alertas de los proyectos asignados, sin poder modificar nada.'],
                    ] },
                    { type: 'p', text: 'Puedes ver tu rol en el menú de usuario (tu avatar, arriba a la derecha).' },
                ],
            },
            {
                id: 'permiso-proyecto',
                title: 'Permiso de lectura y de escritura en un proyecto',
                keywords: ['lectura', 'escritura', 'propietario', 'compartir', 'asignado'],
                blocks: [
                    { type: 'list', items: [
                        '**Lectura**: ver los honeytokens, eventos, solicitudes y estadísticas del proyecto.',
                        '**Escritura**: además, crear, editar y eliminar honeytokens, registrar acciones sobre eventos, cambiar el estado de las alertas de eventos y trabajar con Detección de Contenido.',
                        '**Propietario** (⭐): puede gestionar el proyecto y los usuarios que tienen acceso.',
                    ] },
                    { type: 'p', text: 'En la lista de proyectos, las columnas **«Permiso»** («Escritura» / «Lectura») y **«Propiedad»** («Admin», «Propietario», «Asignado») te dicen qué puedes hacer en cada uno. En la pestaña «Información» de un proyecto ves quién tiene acceso.' },
                    { type: 'warning', text: 'Un usuario con rol **Lector** solo puede tener permiso de lectura, aunque se le asigne a un proyecto.' },
                ],
            },
            {
                id: 'no-puedo',
                title: '¿Por qué no puedo hacer algo?',
                keywords: ['403', 'no autorizado', 'sin permisos', 'botón desactivado', 'error permisos'],
                blocks: [
                    { type: 'list', items: [
                        'Si no ves un botón (por ejemplo «Crear Honeytoken» o «Editar»), tu rol o tu permiso en ese proyecto no lo permiten.',
                        'En algunas pantallas el botón aparece pero, al usarlo, recibes un mensaje de que no tienes permisos: el servidor comprueba siempre tu permiso en el proyecto.',
                        'Las secciones «Catálogos» y «Administración» solo aparecen a los administradores.',
                    ] },
                    { type: 'tip', text: 'Si necesitas escribir en un proyecto, pide a su propietario o a un administrador que te dé permiso de escritura.' },
                ],
            },
        ],
    },

    // ------------------------------------------------------------------ PROYECTOS
    {
        id: 'proyectos',
        title: 'Proyectos',
        icon: 'folder_shared',
        summary: 'Agrupa tus honeytokens, compártelos con tu equipo y consulta todo lo que ocurre en cada proyecto.',
        articles: [
            {
                id: 'lista',
                title: 'La lista de proyectos',
                keywords: ['proyectos', 'filtros', 'exportar', 'csv', 'json', 'todos los proyectos', 'mis proyectos'],
                blocks: [
                    { type: 'p', text: 'En **«Proyectos»** ves los proyectos a los que tienes acceso (todos, si eres administrador), con tarjetas de resumen, filtros por nombre, estado y fechas, y las columnas de tu permiso y tu propiedad en cada uno.' },
                    { type: 'p', text: 'Haz clic en el **nombre** de un proyecto para abrir su página. El menú **⋮** de cada fila tiene:' },
                    { type: 'list', items: [
                        '«Ver informe»: abre las estadísticas del proyecto.',
                        '«Ver honeytokens»: abre la página del proyecto.',
                        '«Ver alertas»: las alertas del proyecto.',
                        '«Editar», «Asignar usuarios» y «Eliminar»: si eres administrador o propietario.',
                    ] },
                    { type: 'p', text: 'Los botones **CSV** y **JSON** exportan la lista tal como la tienes filtrada.' },
                    { type: 'links', links: [{ label: 'Ir a Proyectos', route: '/projects' }] },
                ],
            },
            {
                id: 'crear',
                title: 'Crear un proyecto',
                keywords: ['nuevo proyecto', 'crear proyecto', 'validez', 'estado'],
                blocks: [
                    { type: 'p', text: 'Pueden crear proyectos el **Administrador** y los **Gestores de Proyectos**.' },
                    { type: 'steps', items: [
                        'En «Proyectos», pulsa **«Crear proyecto»** (o usa «Crear Nuevo» › «Nuevo Proyecto» en la barra lateral).',
                        'Escribe el **nombre** (entre 3 y 100 caracteres) y, si quieres, una descripción.',
                        'Indica el **rango de validez**: fecha de inicio (obligatoria) y de fin.',
                        'En **«Compartir proyecto»**, añade a las personas que lo van a usar y elige su permiso (lectura o escritura).',
                        'Pulsa **«Crear proyecto»**.',
                    ] },
                ],
            },
            {
                id: 'compartir',
                title: 'Compartir un proyecto (asignar usuarios)',
                keywords: ['asignar usuarios', 'compartir', 'permiso', 'propietario', 'equipo'],
                blocks: [
                    { type: 'steps', items: [
                        'En la lista de proyectos, abre el menú ⋮ del proyecto y pulsa **«Asignar usuarios»** (o «Editar»).',
                        'En «Compartir proyecto», pulsa **«Añadir usuario»** y búscalo por nombre o email.',
                        'Elige su permiso: 🔍 **lectura** o ✏️ **escritura**. La ⭐ marca al **propietario**.',
                        'Guarda el proyecto.',
                    ] },
                    { type: 'warning', text: 'Los usuarios con rol Lector solo pueden recibir permiso de lectura.' },
                ],
            },
            {
                id: 'pagina',
                title: 'La página de un proyecto',
                keywords: ['pestañas', 'honeytokens', 'eventos', 'solicitudes', 'estadísticas', 'información', 'selector de proyecto'],
                blocks: [
                    { type: 'p', text: 'La página de un proyecto reúne todo lo que depende de él, en pestañas:' },
                    { type: 'table', headers: ['Pestaña', 'Qué contiene'], rows: [
                        ['**Honeytokens**', 'Los señuelos del proyecto, con buscador, acciones y exportación.'],
                        ['**Eventos**', 'Los eventos de seguridad de todos sus honeytokens.'],
                        ['**Solicitudes**', 'Sus solicitudes de Detección de Contenido.'],
                        ['**Estadísticas**', 'Análisis IA de los eventos y análisis geográfico de las IPs, descargable en PDF.'],
                        ['**Información**', 'Datos del proyecto y usuarios con acceso.'],
                    ] },
                    { type: 'list', items: [
                        'El **selector** bajo la cabecera cambia a otro proyecto manteniendo la pestaña en la que estás.',
                        'Con permiso de escritura verás el botón **«Crear Honeytoken»**, que crea el señuelo directamente en este proyecto.',
                    ] },
                ],
            },
            {
                id: 'editar-eliminar',
                title: 'Editar o eliminar un proyecto',
                keywords: ['editar proyecto', 'eliminar proyecto', 'borrar', 'delete'],
                blocks: [
                    { type: 'p', text: 'Desde el menú ⋮ de la lista: **«Editar»** cambia el nombre, las fechas, el estado, la descripción o los usuarios; **«Eliminar»** borra el proyecto.' },
                    { type: 'warning', text: 'Eliminar un proyecto borra también **todos sus honeytokens, eventos y datos asociados** y no se puede deshacer. Para confirmarlo hay que escribir `DELETE`.' },
                ],
            },
        ],
    },

    // ------------------------------------------------------------------ HONEYTOKENS
    {
        id: 'honeytokens',
        title: 'Honeytokens',
        icon: 'token',
        summary: 'Tipos de señuelo, cómo crearlos, desplegarlos y consultar su actividad.',
        articles: [
            {
                id: 'tipos',
                title: '¿Qué tipo de honeytoken elijo?',
                keywords: ['tipos', 'docx', 'xlsx', 'word', 'excel', 'exe', 'ssh', 'carpeta', 'html', 'redirect', 'imagen', 'video', 'pixel', 'javascript', 'js beacon', 'correo', 'mail', 'qr'],
                blocks: [
                    { type: 'p', text: 'Elige el señuelo según **dónde** quieres detectar el acceso:' },
                    { type: 'table', headers: ['Tipo', 'Qué obtienes', 'Avisa cuando…'], rows: [
                        ['**Documento Word señuelo**', 'Un archivo .docx para descargar', 'alguien lo abre en Word'],
                        ['**Documento Excel señuelo**', 'Un archivo .xlsx para descargar', 'alguien lo abre en Excel'],
                        ['**Ejecutable señuelo**', 'Un .exe para Windows con el icono que elijas (carpeta, Word, Excel, PDF…)', 'alguien lo ejecuta'],
                        ['**Carpeta señuelo**', 'Un ZIP con la carpeta y un README', 'alguien abre su contenido'],
                        ['**Servidor señuelo SSH**', 'Un script Python que levanta un SSH falso (puerto 2222 por defecto)', 'alguien intenta entrar: registra IP, usuario y contraseña probados'],
                        ['**Página HTML señuelo**', 'Una URL y su código HTML (y QR)', 'alguien visita la página'],
                        ['**Redirection Token**', 'Un enlace trampa que redirige a la URL que indiques (y QR)', 'alguien abre el enlace'],
                        ['**Imagen señuelo**', 'Una URL y código para incrustar la imagen (y QR)', 'se carga la imagen'],
                        ['**Video señuelo**', 'Una URL y código para incrustar el vídeo (y QR)', 'alguien reproduce, pausa o avanza el vídeo'],
                        ['**Pixel transparente**', 'Un píxel invisible de 1×1 (URL y código HTML)', 'se carga la página o el correo que lo contiene'],
                        ['**JavaScript Token (JS Beacon)**', 'Un fragmento de JavaScript', 'se carga la web en la que lo pegas'],
                        ['**Correo señuelo**', 'Una dirección de correo', 'llega un correo a esa dirección'],
                    ] },
                    { type: 'tip', text: 'Los documentos y ejecutables funcionan bien en carpetas compartidas, repositorios o copias de seguridad; los enlaces, píxeles y JS en webs, correos o documentos internos; el SSH en redes donde un intruso buscaría servidores.' },
                ],
            },
            {
                id: 'crear',
                title: 'Crear un honeytoken',
                keywords: ['nuevo honeytoken', 'crear honeytoken', 'etiquetas', 'agrupación de alertas', 'dominio'],
                blocks: [
                    { type: 'p', text: 'Necesitas **permiso de escritura** en el proyecto.' },
                    { type: 'steps', items: [
                        'Abre el proyecto y pulsa **«Crear Honeytoken»** (o usa «Honeytokens» › elige el proyecto › «Crear Honeytoken», o «Crear Nuevo» › «Nuevo Honeytoken»).',
                        'Elige el **tipo de honeytoken** y comprueba el **proyecto**.',
                        'Escribe un **nombre** que te ayude a reconocerlo (por ejemplo, dónde lo vas a dejar) y, si quieres, **etiquetas**.',
                        'Elige la **agrupación de alertas**: «Por visita individual» (una alerta por acceso), «Por minutos» o «Por sesión completa», y en estos dos casos la **ventana en minutos** (ver «Agrupar alertas» en la sección Alertas).',
                        'Rellena los campos propios del tipo (dominio, ruta, URL de destino, imagen, sistema operativo…).',
                        'Pulsa **«Crear Honeytoken»**. En los documentos y ejecutables el archivo se genera en ese momento (puede tardar hasta un minuto).',
                        'Sigue las indicaciones de **«Próximos pasos»**: descarga el archivo o copia la URL / el código.',
                    ] },
                    { type: 'tip', text: 'Los dominios disponibles para los tipos web los gestiona el administrador en «Administración» › «Dominios».' },
                ],
            },
            {
                id: 'desplegar',
                title: 'Buenas prácticas para desplegar señuelos',
                keywords: ['desplegar', 'buenas prácticas', 'consejos', 'nombre del archivo', 'ubicación'],
                blocks: [
                    { type: 'list', items: [
                        'Ponles **nombres creíbles y atractivos** para un intruso (por ejemplo «Nóminas 2026.xlsx» o «credenciales_vpn.docx»), y renombra los ejecutables, que se generan con nombre aleatorio.',
                        'Colócalos **donde nadie legítimo tenga motivos para tocarlos**: así cada evento es una alerta fiable.',
                        '**Avisa a tu equipo** de que existen, para que no los abran por curiosidad y generen falsos positivos.',
                        'Usa **un honeytoken por ubicación**: si se activa, sabrás exactamente por dónde ha pasado el intruso.',
                        'Revisa periódicamente su **estado**: un honeytoken «Pausado» o «Inactivo» no genera eventos.',
                    ] },
                ],
            },
            {
                id: 'lista',
                title: 'La lista de honeytokens',
                keywords: ['honeytokens', 'filtros', 'tipo', 'estado', 'exportar'],
                blocks: [
                    { type: 'p', text: 'En **«Honeytokens»** elige primero un **proyecto**; después puedes filtrar por tipo, estado o texto. La tabla es la misma en todas partes (aquí, en la página del proyecto y en una solicitud de Detección de Contenido): nombre, tipo, dominio, estado, número de eventos y fecha de creación.' },
                    { type: 'p', text: 'El menú **⋮** de cada honeytoken tiene «Ver informe», «Ver detalles», «Eventos de seguridad» y, con permiso de escritura, «Editar» y «Eliminar».' },
                    { type: 'links', links: [{ label: 'Ir a Honeytokens', route: '/honeytokens-management' }] },
                ],
            },
            {
                id: 'pagina',
                title: 'La página de un honeytoken',
                keywords: ['detalles', 'qr', 'descargar', 'copiar url', 'estadísticas honeytoken', 'json'],
                blocks: [
                    { type: 'p', text: 'Haz clic en el nombre de un honeytoken para abrir su página, con tres pestañas:' },
                    { type: 'table', headers: ['Pestaña', 'Qué contiene'], rows: [
                        ['**Eventos**', 'Los eventos que ha generado (se abre por defecto).'],
                        ['**Detalles**', 'Su URL, código o archivo para descargar, con botones de copiar; el **código QR** en los tipos web; y todos sus datos en JSON.'],
                        ['**Estadísticas**', 'Métricas, severidad, tipos de ataque, IPs atacantes y mapa de orígenes, descargable en PDF.'],
                    ] },
                    { type: 'p', text: 'Con permiso de escritura tienes el botón **«Editar»**. Las migas te devuelven a su proyecto.' },
                ],
            },
            {
                id: 'editar-eliminar',
                title: 'Editar, pausar o eliminar un honeytoken',
                keywords: ['editar', 'pausar', 'desactivar', 'eliminar', 'estado del token'],
                blocks: [
                    { type: 'list', items: [
                        '**Pausar o desactivar**: edita el honeytoken y cambia el «Estado del token» a «Pausado» o «Inactivo». Deja de generar eventos, pero conservas su historial.',
                        '**Eliminar**: desde el menú ⋮ › «Eliminar». Para confirmarlo hay que escribir `DELETE`.',
                    ] },
                    { type: 'warning', text: 'Si eliminas un honeytoken que sigue desplegado, los accesos a ese señuelo ya no se registrarán.' },
                ],
            },
        ],
    },

    // ------------------------------------------------------------------ EVENTOS
    {
        id: 'eventos',
        title: 'Eventos de seguridad',
        icon: 'shield',
        summary: 'Cada acceso a un señuelo: cómo encontrarlo, analizarlo y dejar constancia de lo que has hecho.',
        articles: [
            {
                id: 'que-es',
                title: '¿Qué es un evento de seguridad?',
                keywords: ['evento', 'severidad', 'estado del análisis'],
                blocks: [
                    { type: 'p', text: 'Un **evento** es un acceso a uno de tus honeytokens: quién (IP, navegador o dispositivo), desde dónde (país, proveedor, VPN o Tor) y qué hizo. Cada evento se analiza automáticamente con IA y con información de la IP, y recibe una **severidad**: 🔴 Crítica, 🟠 Alta, 🟡 Media, 🔵 Baja o ⚪ Sin clasificar.' },
                    { type: 'p', text: 'El **estado** indica cómo va su análisis: Detectado, Análisis completado, Análisis parcial o Análisis fallido.' },
                ],
            },
            {
                id: 'buscar',
                title: 'Buscar y filtrar eventos',
                keywords: ['filtros', 'proyecto', 'honeytoken', 'fechas', 'acción', 'exportar'],
                blocks: [
                    { type: 'p', text: 'En **«Eventos de seguridad»** puedes filtrar por proyecto, honeytoken, estado, severidad, acción tomada y fechas. La línea «Vista actual» resume lo que estás viendo.' },
                    { type: 'list', items: [
                        'El **ID** del evento abre su informe; el **honeytoken** y el **proyecto** abren sus páginas.',
                        'La **IP de origen** abre el análisis de esa IP.',
                        'Los botones CSV y JSON exportan los eventos.',
                    ] },
                    { type: 'tip', text: 'También ves los eventos de un proyecto, de un honeytoken o de una solicitud en la pestaña «Eventos» de su página.' },
                    { type: 'links', links: [{ label: 'Ir a Eventos de seguridad', route: '/security-events' }] },
                ],
            },
            {
                id: 'informe',
                title: 'El informe de un evento',
                keywords: ['informe', 'report', 'sigma', 'kill chain', 'm1', 'm6', 'threat score', 'geolocalización'],
                blocks: [
                    { type: 'p', text: 'El informe (ID del evento, o menú › «Ver informe») tiene tres pestañas, descargables en PDF:' },
                    { type: 'list', items: [
                        '**«Análisis IA de eventos»**: puntuación de amenaza, severidad y confianza; resultado de los módulos de análisis (lista negra, reglas Sigma, recurrencia, payload, anomalías y correos) y la fase de la **Cyber Kill Chain**.',
                        '**«Análisis Geo de IPs»**: riesgo de la IP, reputación, ubicación, red, y si viene de VPN, proxy o Tor.',
                        '**«Alertas»**: las alertas que ha generado, para gestionarlas desde aquí.',
                    ] },
                    { type: 'p', text: '«Ver detalles» abre todos los datos técnicos del evento (y el JSON completo).' },
                    { type: 'tip', text: 'Si no conoces un término (Sigma, IOC, Kill Chain…), búscalo en el Glosario de Seguridad.' },
                    { type: 'links', links: [{ label: 'Abrir el Glosario', route: '/glossary' }] },
                ],
            },
            {
                id: 'registrar-accion',
                title: 'Registrar una acción de respuesta',
                keywords: ['registrar acción', 'respuesta', 'bloqueado', 'falso positivo', 'investigado', 'mitigado'],
                blocks: [
                    { type: 'p', text: 'Deja constancia de lo que has hecho ante un evento (necesitas permiso de escritura en su proyecto):' },
                    { type: 'steps', items: [
                        'En la fila del evento, abre el menú y pulsa **«Registrar Acción»**.',
                        'Elige el **tipo de acción**: investigado, bloqueado, lista blanca, escalado, resuelto, falso positivo, en proceso, cuarentena o mitigado.',
                        'Describe lo que has hecho, el **resultado** (exitosa, fallida o pendiente) y, si quieres, notas.',
                        'Guarda. La acción aparece en la columna «Acción Tomada» y puedes filtrar por ella.',
                    ] },
                ],
            },
        ],
    },

    // ------------------------------------------------------------------ ALERTAS
    {
        id: 'alertas',
        title: 'Alertas',
        icon: 'notifications_active',
        summary: 'Las situaciones que requieren tu atención y cómo gestionarlas hasta resolverlas.',
        articles: [
            {
                id: 'que-es',
                title: '¿Qué es una alerta?',
                keywords: ['alerta', 'viaje imposible', 'país de riesgo', 'crítica', 'alta'],
                blocks: [
                    { type: 'p', text: 'Una **alerta** avisa de una situación que requiere atención. Pueden venir de dos fuentes:' },
                    { type: 'list', items: [
                        '**Eventos de seguridad**: el análisis por IA de un evento lo considera grave.',
                        '**Geográficas**: la IP de origen indica un «Viaje Imposible» (accesos desde lugares demasiado lejanos en poco tiempo) o un «País de Riesgo».',
                    ] },
                    { type: 'p', text: 'La pantalla de alertas muestra las de severidad **crítica y alta**.' },
                ],
            },
            {
                id: 'gestionar',
                title: 'Gestionar alertas',
                keywords: ['estados', 'reconocer', 'investigar', 'resolver', 'falso positivo', 'marcar leída', 'acciones masivas'],
                blocks: [
                    { type: 'p', text: 'Cada alerta pasa por estos **estados**:' },
                    { type: 'table', headers: ['Estado', 'Significa'], rows: [
                        ['Nueva', 'Todavía nadie la ha revisado.'],
                        ['Reconocida', 'Alguien la ha visto y se hace cargo.'],
                        ['En investigación', 'Se está analizando.'],
                        ['Resuelta', 'El incidente está cerrado.'],
                        ['Falso positivo', 'No era una amenaza real.'],
                    ] },
                    { type: 'steps', items: [
                        'En **«Alertas»**, filtra por proyecto, honeytoken, severidad, estado, tipo, país o fechas.',
                        'Abre **«Ver Detalle»** para ver la IP (proveedor, reputación), el honeytoken y el evento.',
                        'Cambia su estado con **«Reconocer»**, **«Investigar»**, **«Resolver»** o **«Falso Positivo»**, añadiendo notas si quieres.',
                        'Para varias a la vez, selecciónalas y usa **«Marcar Leídas»**, **«Actualizar Estado»** o **«Resolver»**.',
                    ] },
                    { type: 'tip', text: 'Cambiar el estado de las alertas de eventos de seguridad requiere permiso de escritura en el proyecto.' },
                    { type: 'links', links: [{ label: 'Ir a Alertas', route: '/alerts' }] },
                ],
            },
            {
                id: 'agrupar',
                title: 'Agrupar alertas de un honeytoken',
                keywords: ['agrupación de alertas', 'agrupar', 'por minutos', 'por sesión', 'ventana', 'accesos', 'ruido'],
                blocks: [
                    { type: 'p', text: 'Cada honeytoken decide cuántas alertas genera con su **agrupación de alertas** (al crearlo o editarlo). Los accesos se registran siempre todos como **eventos**; lo que se agrupa son las **alertas**.' },
                    { type: 'table', headers: ['Agrupación', 'Qué hace'], rows: [
                        ['**Por visita individual**', 'Una alerta por cada acceso.'],
                        ['**Por minutos**', 'Los accesos de cualquier origen durante la ventana elegida, contada desde el primero, forman una sola alerta. El siguiente acceso fuera de la ventana abre otra.'],
                        ['**Por sesión completa**', 'Los accesos del mismo visitante (misma IP y mismo navegador) forman una sola alerta mientras no pase la ventana sin actividad.'],
                    ] },
                    { type: 'list', items: [
                        'La **ventana** la eliges en minutos (de 1 a 1440). Si la dejas vacía: 15 minutos por minutos y 30 por sesión.',
                        'Una alerta agrupada muestra **«N accesos»**; en su detalle verás el periodo que cubre y un enlace al último acceso.',
                        'Si llega un acceso más grave, la alerta pasa a representarlo y vuelve a quedar **sin leer**.',
                        'Una alerta ya **resuelta o cerrada** no sigue acumulando: el siguiente acceso abre una nueva.',
                    ] },
                ],
            },
            {
                id: 'avisos',
                title: 'El aviso rojo y la campana',
                keywords: ['banner', 'aviso rojo', 'campana', 'notificaciones', 'revisar ahora', 'alta prioridad'],
                blocks: [
                    { type: 'list', items: [
                        'La **barra roja** «N alertas nuevas de alta prioridad» aparece bajo la cabecera cuando hay alertas críticas nuevas. «Revisar Ahora» te lleva a ellas; la X la oculta durante una hora (vuelve antes si llegan alertas nuevas).',
                        'La **campana** de la barra superior muestra tus últimas alertas críticas o altas sin revisar. Puedes marcarlas como leídas una a una o todas a la vez.',
                    ] },
                ],
            },
        ],
    },

    // ------------------------------------------------------------------ INTELIGENCIA
    {
        id: 'inteligencia',
        title: 'Inteligencia de amenazas',
        icon: 'travel_explore',
        summary: 'Investiga una IP, un dominio o un evento concreto con fuentes de reputación y análisis por IA.',
        articles: [
            {
                id: 'ips',
                title: 'Búsqueda de IPs',
                keywords: ['ip', 'dominio', 'reputación', 'ioc', 'geolocalización', 'isp'],
                blocks: [
                    { type: 'steps', items: [
                        'En **«Búsqueda de IPs»**, escribe una **dirección IP o un dominio** y busca.',
                        'Revisa su **geolocalización** (ubicación, proveedor, coordenadas, zona horaria).',
                        'Revisa su **reputación**: si aparece como indicador de compromiso (IOC), con la fuente, los tipos de amenaza y el nivel de confianza; o «IP Limpia».',
                        'En **«Eventos de Seguridad en Tu Sistema»** ves si esa IP ya ha tocado tus honeytokens; «Ver Todos» abre esos eventos.',
                    ] },
                    { type: 'tip', text: 'Desde la tabla de eventos, haz clic en la IP de origen para analizarla directamente.' },
                    { type: 'links', links: [{ label: 'Ir a Búsqueda de IPs', route: '/threat-intelligence/ip-analysis' }] },
                ],
            },
            {
                id: 'eventos',
                title: 'Análisis de eventos',
                keywords: ['análisis de eventos', 'id del evento', 'eventos relacionados', 'acciones recomendadas'],
                blocks: [
                    { type: 'p', text: 'En **«Análisis de eventos»** introduce el **ID de un evento** para ver en una sola pantalla las métricas de riesgo de su IP, la evaluación de los módulos de análisis, los **eventos relacionados** desde la misma IP y las **acciones recomendadas**.' },
                    { type: 'links', links: [{ label: 'Ir a Análisis de eventos', route: '/threat-intelligence/event-analysis' }] },
                ],
            },
        ],
    },

    // ------------------------------------------------------------------ DETECCIÓN DE CONTENIDO
    {
        id: 'deteccion-contenido',
        title: 'Detección de Contenido',
        icon: 'article',
        summary: 'Publica artículos con honeytokens integrados para saber quién consulta y reutiliza ese contenido.',
        articles: [
            {
                id: 'que-es',
                title: '¿Qué es la Detección de Contenido?',
                keywords: ['detección de contenido', 'artículo', 'publicación', 'noticia', 'cms'],
                blocks: [
                    { type: 'p', text: 'La Detección de Contenido genera con IA un **artículo** sobre una entidad (una persona, empresa, producto o plataforma), lo **publica** en una web de noticias e incluye en él **honeytokens** invisibles. Cuando alguien lee el artículo, lo copia o descarga sus recursos, sus honeytokens generan eventos: así sabes quién se interesa por esa entidad y desde dónde.' },
                    { type: 'p', text: 'El proceso tiene cuatro pasos:' },
                    { type: 'steps', items: [
                        '**Solicitud**: describes la entidad y el enfoque.',
                        '**Borrador**: la IA redacta el artículo a partir de tu descripción y de las fuentes sugeridas.',
                        '**Revisión editorial**: una persona lo revisa, lo edita si hace falta y lo aprueba o lo rechaza.',
                        '**Publicación y seguimiento**: el artículo se publica con sus honeytokens y ves sus eventos y estadísticas.',
                    ] },
                    { type: 'p', text: 'Todas las acciones requieren **permiso de escritura** en el proyecto de la solicitud.' },
                    { type: 'links', links: [{ label: 'Ir a Solicitudes', route: '/detection-content/requests' }] },
                ],
            },
            {
                id: 'crear-solicitud',
                title: 'Crear una solicitud',
                keywords: ['nueva solicitud', 'entidad', 'resumen', 'descripción', 'categoría', 'etiquetas', 'fuentes sugeridas', 'notas editoriales'],
                blocks: [
                    { type: 'steps', items: [
                        'En **«Solicitudes»**, pulsa **«Nueva solicitud»**.',
                        'Elige el **proyecto** al que pertenecerá (sus honeytokens y eventos se verán también en ese proyecto).',
                        'Escribe el **nombre de la entidad** y su **tipo** (Persona, Empresa, Producto, Plataforma u Otro).',
                        'Redacta el **resumen ejecutivo** y la **descripción detallada**: es la información de la que partirá la IA, así que cuanto más precisa, mejor el artículo.',
                        'Opcionalmente, elige una **categoría**, añade **etiquetas**, **fuentes sugeridas** (URLs que la IA usará como referencia) y **notas editoriales** (indicaciones de tono o enfoque).',
                        'Pulsa **«Crear Solicitud»**.',
                    ] },
                    { type: 'tip', text: 'Las fuentes sugeridas se comprueban al generar el borrador: usa URLs públicas que carguen correctamente.' },
                ],
            },
            {
                id: 'borrador',
                title: 'Generar el borrador con IA',
                keywords: ['generar borrador', 'ia', 'tarda', 'esperar', 'versión', 'no aparece el borrador'],
                blocks: [
                    { type: 'steps', items: [
                        'Abre la solicitud (su nombre, o menú › «Ver flujo completo») y pulsa **«Generar borrador con IA»**.',
                        'Espera a que termine: suele tardar **entre 2 y 10 minutos**. Verás el progreso y el tiempo transcurrido.',
                        'Cuando esté listo, la solicitud pasa a «Borrador generado».',
                    ] },
                    { type: 'warning', text: 'La generación sigue en el servidor aunque cierres la ventana: si la cierras, recarga la solicitud pasados unos minutos. **No la lances otra vez** mientras tanto, o se crearán varias versiones del borrador.' },
                ],
            },
            {
                id: 'revision',
                title: 'Revisar, editar y aprobar el borrador',
                keywords: ['vista previa', 'editar borrador', 'aprobar', 'rechazar', 'regenerar', 'markdown', 'revisión editorial'],
                blocks: [
                    { type: 'list', items: [
                        '**«Ver vista previa»**: el artículo tal como se publicará, con sus fuentes y referencias.',
                        '**«Editar borrador»**: cambia el título y el contenido (en Markdown) y guarda.',
                        '**«Aprobar borrador»**: lo deja listo para publicar. Al aprobarlo se crea automáticamente un honeytoken `[DC] <entidad>` vinculado a la solicitud.',
                        '**«Rechazar»**: indica el motivo. Si marcas «Regenerar automáticamente con IA usando este feedback», la IA redactará una nueva versión teniéndolo en cuenta.',
                    ] },
                    { type: 'tip', text: 'Antes de aprobar, comprueba que el texto es veraz, no daña la reputación de la entidad y no incluye datos que no se deban publicar.' },
                ],
            },
            {
                id: 'publicar',
                title: 'Publicar el artículo',
                keywords: ['publicar', 'publicar ahora', 'cms', 'web de noticias', 'recursos'],
                blocks: [
                    { type: 'steps', items: [
                        'Con la solicitud en «Aprobado para publicar», pulsa **«Publicar ahora»**.',
                        'Revisa los **recursos asociados**: los honeytokens que se incluirán en el artículo (automáticos y manuales).',
                        'Publica. Al terminar verás la **URL del artículo** y, por cada honeytoken, su URL de seguimiento.',
                    ] },
                    { type: 'p', text: 'La web de noticias en la que se publica se elige automáticamente entre las conexiones configuradas.' },
                ],
            },
            {
                id: 'seguimiento',
                title: 'Seguir una publicación: honeytokens, eventos y estadísticas',
                keywords: ['seguimiento', 'honeytokens desplegados', 'eventos', 'estadísticas', 'visitas', 'referentes'],
                blocks: [
                    { type: 'p', text: 'La página de una solicitud tiene cuatro pestañas (también accesibles desde el menú ⋮ de la lista):' },
                    { type: 'table', headers: ['Pestaña', 'Qué contiene'], rows: [
                        ['**Flujo**', 'Los pasos de la solicitud y las acciones disponibles en cada momento.'],
                        ['**Honeytokens**', 'Los honeytokens desplegados con la publicación (la misma tabla que en el resto de la aplicación), con su **origen**: «Automático» (creado al aprobar) o «Manual».'],
                        ['**Eventos**', 'Los eventos que han generado esos honeytokens.'],
                        ['**Estadísticas**', 'Eventos de seguridad, honeytokens activos, visitas y riesgo; eventos por día y por honeytoken; principales referentes de las visitas.'],
                    ] },
                    { type: 'tip', text: 'Las solicitudes de un proyecto también aparecen en la pestaña «Solicitudes» de la página del proyecto.' },
                ],
            },
            {
                id: 'sin-rastreador',
                title: 'Aviso: «La publicación no incluye el rastreador…»',
                keywords: ['rastreador', 'tracker', 'no registra eventos', 'regenerar', 'url de seguimiento', 'sin eventos'],
                blocks: [
                    { type: 'p', text: 'Para que un honeytoken avise, el artículo publicado tiene que llevar su **rastreador** (su URL de seguimiento). Si al crearlo o al publicar no se pudo generar esa URL —por ejemplo, porque el servicio de honeytokens no respondía—, el artículo se publica sin él y **las visitas no generan eventos** de ese honeytoken.' },
                    { type: 'p', text: 'En ese caso, las pestañas Honeytokens y Estadísticas muestran un **aviso rojo** con el motivo de cada honeytoken afectado.' },
                    { type: 'steps', items: [
                        'Pulsa **«Regenerar y actualizar la publicación»** (necesitas permiso de escritura).',
                        'Se generan las URLs que faltan y se intenta actualizar el artículo ya publicado, sin crear otro.',
                        'Si todo va bien, verás «Rastreadores regenerados y publicación actualizada» y el aviso desaparece.',
                    ] },
                    { type: 'warning', text: 'Algunas webs de noticias no permiten editar un artículo ya publicado: verás «…no se pudo actualizar la publicación». En ese caso, la alternativa es crear una nueva solicitud y publicarla.' },
                ],
            },
        ],
    },

    // ------------------------------------------------------------------ ADMINISTRACIÓN
    {
        id: 'administracion',
        title: 'Administración',
        icon: 'settings',
        summary: 'Usuarios, dominios y catálogos. Solo para administradores.',
        adminOnly: true,
        articles: [
            {
                id: 'usuarios',
                title: 'Usuarios',
                keywords: ['usuarios', 'nuevo usuario', 'rol', 'contraseña', 'alta'],
                blocks: [
                    { type: 'p', text: 'En **«Administración» › «Usuarios»** el administrador da de alta, edita y elimina usuarios.' },
                    { type: 'steps', items: [
                        'Pulsa **«Añadir»**.',
                        'Escribe el nombre completo, el correo y una contraseña (mínimo 6 caracteres).',
                        'Elige el **rol** (por defecto, Lector).',
                        'Guarda. Después, asígnale los proyectos en los que debe trabajar desde la edición de cada proyecto.',
                    ] },
                    { type: 'links', links: [
                        { label: 'Ir a Usuarios', route: '/users' },
                        { label: 'Ver los roles', route: '/ayuda/roles-permisos' },
                    ] },
                ],
            },
            {
                id: 'dominios',
                title: 'Dominios',
                keywords: ['dominios', 'dominio', 'web'],
                blocks: [
                    { type: 'p', text: 'En **«Administración» › «Dominios»** se gestionan los dominios web que se ofrecen en el campo «Dominio» al crear honeytokens de tipo web (página HTML, redirección, imagen, vídeo, píxel o JavaScript).' },
                    { type: 'links', links: [{ label: 'Ir a Dominios', route: '/administration/domains' }] },
                ],
            },
            {
                id: 'catalogos',
                title: 'Catálogos: tipos de honeytokens y de ataques',
                keywords: ['catálogos', 'tipos de ataques', 'subtipos', 'kill chain', 'relaciones'],
                blocks: [
                    { type: 'p', text: 'Los **catálogos** describen los tipos de honeytokens, los **tipos de ataque** (organizados por fases de la Cyber Kill Chain) y sus **subtipos**, y las relaciones entre ellos. Se usan para clasificar los eventos.' },
                    { type: 'p', text: 'Desde cada catálogo puedes consultar los detalles y gestionar las relaciones entre tipos de ataque, subtipos y tipos de honeytoken. Las altas se hacen mediante importación de datos maestros (CSV).' },
                ],
            },
        ],
    },

    // ------------------------------------------------------------------ FAQ
    {
        id: 'preguntas-frecuentes',
        title: 'Preguntas frecuentes',
        icon: 'quiz',
        summary: 'Respuestas rápidas a las dudas más habituales.',
        articles: [
            {
                id: 'sin-eventos',
                title: 'He desplegado un honeytoken y no veo eventos',
                keywords: ['no aparecen eventos', 'no funciona', 'no registra'],
                blocks: [
                    { type: 'list', items: [
                        'Comprueba que su **estado** es «Activo»: los pausados e inactivos no registran nada.',
                        'Comprueba que has desplegado **la última versión** (el archivo descargado o la URL / el código actuales).',
                        'Algunos entornos bloquean el aviso: por ejemplo, un documento abierto sin conexión a Internet o un correo que no carga imágenes no podrán avisar hasta que tengan conexión o se carguen.',
                        'En Detección de Contenido, mira si la solicitud muestra el aviso «La publicación no incluye el rastreador…».',
                    ] },
                ],
            },
            {
                id: 'no-veo-proyecto',
                title: 'No veo un proyecto que debería ver',
                keywords: ['no veo proyecto', 'acceso', 'asignar'],
                blocks: [
                    { type: 'p', text: 'Solo ves los proyectos a los que te han asignado (salvo que seas administrador). Pide a su propietario o a un administrador que te añada desde «Asignar usuarios».' },
                ],
            },
            {
                id: 'falsos-positivos',
                title: '¿Cómo evito falsos positivos?',
                keywords: ['falso positivo', 'ruido'],
                blocks: [
                    { type: 'p', text: 'Avisa a tu equipo de dónde están los señuelos y colócalos donde nadie legítimo tenga que entrar. Si un señuelo recibe accesos repetidos, usa la **agrupación de alertas** por minutos o por sesión para recibir una alerta por grupo en lugar de una por acceso. En un JavaScript Token puedes además limitar los **dominios permitidos**. Si una alerta no era real, márcala como «Falso Positivo».' },
                ],
            },
            {
                id: 'informe-pdf',
                title: '¿Puedo sacar un informe?',
                keywords: ['pdf', 'informe', 'exportar', 'csv', 'json'],
                blocks: [
                    { type: 'p', text: 'Sí: los informes de Inicio, de cada proyecto, de cada honeytoken y de cada evento tienen el botón **«Descargar PDF»**. Las tablas de proyectos, honeytokens y eventos se pueden exportar en **CSV** o **JSON**.' },
                ],
            },
            {
                id: 'glosario',
                title: 'No entiendo un término',
                keywords: ['glosario', 'término', 'definición'],
                blocks: [
                    { type: 'p', text: 'El **Glosario de Seguridad** explica los conceptos de la aplicación (honeytoken, IOC, reglas Sigma, MITRE ATT&CK, Cyber Kill Chain, puntuaciones de riesgo…) con ejemplos.' },
                    { type: 'links', links: [{ label: 'Abrir el Glosario', route: '/glossary' }] },
                ],
            },
        ],
    },
];
